import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { collectionTiers as staticTiers } from "../data/siteData";
import { optimizeImg } from "../lib/img";
import { useZeoContent } from "../lib/ZeoContentContext";

// Panelde tanımlı koleksiyon kartı varsa onu kullanır (harici bir bağlantıya
// "to" olarak yönlendirir); hiç tanımlanmamışsa yerleşik örnek kartlara düşer
// — bu sayede yeni bir işletme panelde hiçbir şey ayarlamadan da boş bir
// bölümle karşılaşmaz.
function useLiveCollections() {
  const [tiers, setTiers] = useState(staticTiers);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/collections.php")
      .then((r) => r.json())
      .then((data) => {
        if (cancelled || data?.status === "error" || !Array.isArray(data?.collections)) return;
        if (data.collections.length === 0) return;
        setTiers(
          data.collections.map((c) => ({
            id: c.id,
            name: c.name,
            desc: c.description || "",
            to: c.link_url || "/urunler",
            badge: c.badge || null,
            image: c.image_url || staticTiers[0]?.image,
          }))
        );
      })
      .catch(() => {
        // ZEO Connect'e ulaşılamadı — statik örnek kartlarla devam edilir.
      });
    return () => { cancelled = true; };
  }, []);

  return tiers;
}

export default function CollectionTiers() {
  const { business } = useZeoContent();
  const tiers = useLiveCollections();

  return (
    <section className="py-16 md:py-20 bg-white" data-testid="collection-tiers-section">
      <div className="frida-container">
        <div className="flex items-end justify-between gap-6 mb-8 md:mb-10">
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#222222] tracking-tight leading-[1.1]" data-testid="collection-tiers-title">
            {business.shortName} <span className="italic text-[#A85C34]">Koleksiyonları</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6" data-testid="collection-tiers-grid">
          {tiers.map((tier) => (
            <Link
              key={tier.id}
              to={tier.to}
              className="group relative rounded-3xl overflow-hidden bg-[#F8F8F8] lift-card block isolate"
              data-testid={`collection-tier-${tier.id}`}
            >
              <div className="relative aspect-[4/3] zoom-on-hover overflow-hidden">
                <img
                  src={optimizeImg(tier.image, 700)}
                  alt={tier.name}
                  width="700"
                  height="525"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
                {tier.badge && (
                  <span className="absolute top-4 right-4 text-[10px] font-semibold tracking-[0.15em] uppercase px-3 py-1.5 rounded-full bg-[#D4A15C] text-white">
                    {tier.badge}
                  </span>
                )}
              </div>
              <div className="p-6 md:p-7">
                <h3 className="font-serif text-2xl text-[#222222] mb-1.5">{tier.name}</h3>
                <p className="text-sm text-[#888888] mb-5">{tier.desc}</p>
                <span className="inline-flex items-center gap-2 text-sm font-medium text-[#A85C34] group-hover:gap-3 transition-all">
                  Keşfet
                  <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
